import { Calendar, AlertTriangle, MapPin, Clock, Info, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import './DeerStalkingPage.css';

export default function DeerStalkingPage() {
  return (
    <div className="deer-stalking-page">
      <div className="deer-header">
        <h1>🦌 Deer Stalking & Jagdzeiten in Schottland</h1>
        <p>Wichtige Informationen für Wanderer & Radfahrer</p>
      </div>

      <div className="deer-content">
        <section className="deer-warning">
          <AlertTriangle size={48} color="#f59e0b" />
          <div>
            <h2>⚠️ Was ist Deer Stalking?</h2>
            <p>Deer Stalking ist die traditionelle Hirschjagd in den schottischen Highlands. Während der Jagdsaison kann es zu <strong>temporären Wegsperrungen</strong> und eingeschränktem Zugang in bestimmten Gebieten kommen.</p>
            <p className="info-text">💡 Das ist KEIN Grund zur Panik! Mit etwas Planung und Rücksichtnahme könnt ihr die Highlands trotzdem genießen.</p>
          </div>
        </section>

        <section className="deer-section">
          <h2><Calendar size={24} /> Jagdzeiten (Stalking Season)</h2>
          <div className="seasons-grid">
            <div className="season-card stag">
              <div className="season-header">
                <h3>🦌 Red Stag (Rothirsch)</h3>
                <div className="season-dates">1. Juli - 20. Oktober</div>
              </div>
              <p><strong>Hauptsaison:</strong> August - Oktober</p>
              <p className="note">Männliche Tiere - Hauptaktivität in den Highlands</p>
            </div>

            <div className="season-card hind">
              <div className="season-header">
                <h3>🦌 Red Hind (Hirschkuh)</h3>
                <div className="season-dates">21. Oktober - 15. Februar</div>
              </div>
              <p><strong>Hauptsaison:</strong> November - Januar</p>
              <p className="note">Weibliche Tiere - meist weniger Einschränkungen</p>
            </div>

            <div className="season-card roe">
              <div className="season-header">
                <h3>🦌 Roe Deer (Rehwild)</h3>
                <div className="season-dates">Apr-Okt (♂) / Okt-März (♀)</div>
              </div>
              <p>Kleineres Wild - weniger Auswirkungen auf Wanderer</p>
            </div>
          </div>
        </section>

        <section className="deer-section impact">
          <h2><Shield size={24} /> Auswirkungen auf Outdoor-Aktivitäten</h2>
          <div className="impact-grid">
            <div className="impact-card high">
              <h4>🔴 Hohe Aktivität</h4>
              <p><strong>August - Oktober</strong></p>
              <ul>
                <li>Mögliche Wegsperrungen</li>
                <li>Empfohlen: Routen vorher checken</li>
                <li>Estate-Websites prüfen</li>
                <li>Früh morgens oder spät abends wandern</li>
              </ul>
            </div>

            <div className="impact-card medium">
              <h4>🟡 Mittlere Aktivität</h4>
              <p><strong>Juli & November-Februar</strong></p>
              <ul>
                <li>Gelegentliche Einschränkungen</li>
                <li>Meist nur lokale Bereiche betroffen</li>
                <li>Flexible Routenplanung empfohlen</li>
              </ul>
            </div>

            <div className="impact-card low">
              <h4>🟢 Niedrige Aktivität</h4>
              <p><strong>März - Juni</strong></p>
              <ul>
                <li>Kaum Einschränkungen</li>
                <li>Beste Zeit für Highlands-Touren</li>
                <li>Volle Zugänglichkeit</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="deer-section">
          <h2><MapPin size={24} /> Betroffene Regionen</h2>
          <div className="regions-list">
            <div className="region">
              <strong>🔴 Stark betroffen:</strong>
              <p>West & Central Highlands, Cairngorms, Glen Affric, Torridon, Knoydart</p>
            </div>
            <div className="region">
              <strong>🟡 Teilweise betroffen:</strong>
              <p>Southern Highlands, Grampians, Monadhliath</p>
            </div>
            <div className="region">
              <strong>🟢 Kaum betroffen:</strong>
              <p>Lowlands, Borders, Isle of Skye (touristisch), Küstenregionen</p>
            </div>
          </div>
        </section>

        <section className="deer-section tips">
          <h2><Info size={24} /> Tipps für Wanderer & Radfahrer</h2>
          <div className="tips-grid">
            <div className="tip">
              <div className="tip-icon">📞</div>
              <h4>1. Vorher informieren</h4>
              <p>Ruf das jeweilige Estate an oder check die Website. Viele Estates zeigen aktive Stalking-Bereiche online.</p>
            </div>

            <div className="tip">
              <div className="tip-icon">🕐</div>
              <h4>2. Timing beachten</h4>
              <p>Stalking findet meist tagsüber (9-16 Uhr) statt. Frühe/späte Touren sind oft unproblematisch.</p>
            </div>

            <div className="tip">
              <div className="tip-icon">🗺️</div>
              <h4>3. Alternative Routen</h4>
              <p>Hab immer Plan B! Viele Gebiete haben alternative Pfade.</p>
            </div>

            <div className="tip">
              <div className="tip-icon">👋</div>
              <h4>4. Respekt zeigen</h4>
              <p>Wenn du Stalker siehst: Warten oder nachfragen. Sie sind meist hilfsbereit!</p>
            </div>

            <div className="tip">
              <div className="tip-icon">🐕</div>
              <h4>5. Hunde an die Leine</h4>
              <p>IMMER während der Stalking-Season! Wildernde Hunde gefährden die Jagd.</p>
            </div>

            <div className="tip">
              <div className="tip-icon">🔇</div>
              <h4>6. Lärm vermeiden</h4>
              <p>Hirsche sind scheu. Unnötiger Lärm stört die Jagd und verärgert Stalker.</p>
            </div>
          </div>
        </section>

        <section className="deer-section resources">
          <h2><Clock size={24} /> Nützliche Ressourcen</h2>
          <div className="resources-grid">
            <a href="https://www.outdooraccess-scotland.scot" target="_blank" rel="noopener noreferrer" className="resource-card">
              <h4>🏴󠁧󠁢󠁳󠁣󠁴󠁿 Scottish Outdoor Access Code</h4>
              <p>Offizielle Richtlinien für verantwortungsvollen Zugang</p>
            </a>

            <a href="https://www.hillphones.info" target="_blank" rel="noopener noreferrer" className="resource-card">
              <h4>📞 Hillphones</h4>
              <p>Telefonnummern von Estates für tagesaktuelle Infos</p>
            </a>

            <a href="https://www.snh.gov.uk" target="_blank" rel="noopener noreferrer" className="resource-card">
              <h4>🌲 NatureScot</h4>
              <p>Naturschutzbehörde mit Zugangs-Infos</p>
            </a>
          </div>
        </section>

        <section className="deer-section myth">
          <h2>❌ Mythen vs ✅ Fakten</h2>
          <div className="myth-grid">
            <div className="myth-item">
              <div className="myth-label">❌ MYTHOS</div>
              <p>"Während Stalking ist der Zugang verboten"</p>
            </div>
            <div className="fact-item">
              <div className="fact-label">✅ FAKT</div>
              <p>Du hast IMMER ein Recht auf Zugang (Scottish Outdoor Access Code). Estates können nur höflich um Umleitung bitten.</p>
            </div>

            <div className="myth-item">
              <div className="myth-label">❌ MYTHOS</div>
              <p>"Stalking ist gefährlich für Wanderer"</p>
            </div>
            <div className="fact-item">
              <div className="fact-label">✅ FAKT</div>
              <p>Stalker sind Profis mit hohem Sicherheitsbewusstsein. Unfälle sind extrem selten.</p>
            </div>
          </div>
        </section>

        <div className="deer-footer">
          <p className="footer-note">
            🦌 <strong>Zusammenleben funktioniert!</strong> Mit gegenseitigem Respekt und etwas Planung können Outdoor-Enthusiasten und Landbesitzer die Highlands gemeinsam genießen.
          </p>
          <Link to="/" className="btn-back">
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}
